/**
 * US Healthcare Billing Code Utilities.
 *
 * Provides validation and code sets for:
 * 1. CMS Place of Service (POS) Codes
 * 2. ICD-10-CM Diagnosis Codes
 * 3. CPT & HCPCS Procedure Codes
 */

// ── 1. CMS Place of Service (POS) Codes ──
// Official 2-digit CMS POS code set
export const CMS_POS_CODES = [
  "01", "02", "03", "04", "05", "06", "07", "08", "09", "10",
  "11", "12", "13", "14", "15", "16", "17", "18", "19", "20",
  "21", "22", "23", "24", "25", "26", "31", "32", "33", "34",
  "41", "42", "49", "50", "51", "52", "53", "54", "55", "56",
  "57", "58", "60", "61", "62", "65", "71", "72", "81", "99",
] as const;

export type CmsPosCode = typeof CMS_POS_CODES[number];

/**
 * Validates if the given code is a valid 2-digit CMS Place of Service (POS) code.
 */
export const isValidPosCode = (code?: string | null): boolean => {
  if (!code || typeof code !== "string") return false;
  const trimmed = code.trim();
  return (CMS_POS_CODES as readonly string[]).includes(trimmed);
};

/**
 * Validates a Place of Service (POS) code input value.
 */
export const validatePosCode = (
  val?: string | null,
  isRequired = false,
  requiredMessage = "Place of Service (POS Code) is required."
): string => {
  if (!val || !val.trim()) {
    return isRequired ? requiredMessage : "";
  }
  const trimmed = val.trim();
  if (!isValidPosCode(trimmed)) {
    return "Please enter a valid 2-digit POS code.";
  }
  return "";
};

// ── 2. ICD-10-CM Diagnosis Code Validation ──
/**
 * Validates an individual ICD-10-CM diagnosis code.
 *
 * Rules:
 * - 3 to 7 characters (or up to 8 with decimal point)
 * - 1st character: Letter (A-Z)
 * - 2nd character: Digit (0-9)
 * - 3rd character: Alphanumeric (0-9, A-Z)
 * - Optional period (.) followed by 1 to 4 alphanumeric characters (0-9, A-Z)
 * - Alternatively unpunctuated format: A-Z followed by 0-9 followed by 1 to 5 alphanumeric characters
 */
export const isValidIcd10Code = (code?: string | null): boolean => {
  if (!code || typeof code !== "string") return false;
  const trimmed = code.trim().toUpperCase();
  return /^[A-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/.test(trimmed) || /^[A-Z][0-9][0-9A-Z]{1,5}$/.test(trimmed);
};

/**
 * Validates single or multiple ICD-10-CM diagnosis codes separated by commas, semicolons, or spaces.
 */
export const validateIcd10Codes = (
  val?: string | null,
  isRequired = false,
  requiredMessage = "ICD-10 Diagnosis Code is required."
): string => {
  if (!val || !val.trim()) {
    return isRequired ? requiredMessage : "";
  }
  const codes = val.trim().split(/[\s,;]+/).filter(Boolean);
  if (codes.length === 0) {
    return isRequired ? requiredMessage : "";
  }
  const allValid = codes.every((c) => isValidIcd10Code(c));
  if (!allValid) {
    return "Please enter a valid ICD-10-CM diagnosis code.";
  }
  return "";
};

// ── 3. CPT & HCPCS Procedure Code Validation ──
/**
 * Validates an individual CPT or HCPCS procedure code.
 *
 * Code Types:
 * - CPT Category I: 5 numeric digits (e.g., 99213, 99214, 36415)
 * - CPT Category II: 4 numeric digits followed by 'F' (e.g., 0001F, 3008F)
 * - CPT Category III: 4 numeric digits followed by 'T' (e.g., 0042T, 0100T)
 * - HCPCS Level II: 1 letter (A-V, X-Z) followed by 4 numeric digits (e.g., G0154, T1019, A0428)
 * - Optional 2-character alphanumeric modifier appended with a hyphen (e.g., 99213-25, T1019-U1)
 */
export const isValidCptHcpcsCode = (code?: string | null): boolean => {
  if (!code || typeof code !== "string") return false;
  const trimmed = code.trim().toUpperCase();

  // CPT Category I: 5 digits, optional 2-character modifier (-25, -59, etc.)
  if (/^\d{5}(-[A-Z0-9]{2})?$/.test(trimmed)) {
    return true;
  }

  // CPT Category II: 4 digits + F, optional modifier
  if (/^\d{4}F(-[A-Z0-9]{2})?$/.test(trimmed)) {
    return true;
  }

  // CPT Category III: 4 digits + T, optional modifier
  if (/^\d{4}T(-[A-Z0-9]{2})?$/.test(trimmed)) {
    return true;
  }

  // HCPCS Level II: 1 letter + 4 digits, optional modifier (e.g., G0154, T1019, A0428)
  if (/^[A-Z]\d{4}(-[A-Z0-9]{2})?$/.test(trimmed)) {
    return true;
  }

  return false;
};

/**
 * Validates single or multiple CPT/HCPCS codes separated by commas, semicolons, or spaces.
 */
export const validateCptHcpcsCodes = (
  val?: string | null,
  isRequired = false,
  requiredMessage = "CPT/HCPCS Code is required."
): string => {
  if (!val || !val.trim()) {
    return isRequired ? requiredMessage : "";
  }
  const codes = val.trim().split(/[\s,;]+/).filter(Boolean);
  if (codes.length === 0) {
    return isRequired ? requiredMessage : "";
  }
  const allValid = codes.every((c) => isValidCptHcpcsCode(c));
  if (!allValid) {
    return "Please enter a valid CPT/HCPCS code.";
  }
  return "";
};

/**
 * Sanitizes and caps CPT/HCPCS code input in real-time.
 * - Converts to uppercase
 * - Allows only [A-Z0-9,;\s-]
 * - Caps each code token:
 *     - Base code: at most 5 characters (e.g. 99213, G0154, 0100T, 2022F)
 *     - Optional modifier(s): each preceded by '-' and at most 2 characters (e.g. -25, -U1)
 * - Prevents entering continuous invalid strings like 675875785875785765675.
 */
export const formatCptHcpcsInput = (input?: string | null): string => {
  if (!input) return "";
  const cleaned = input.toUpperCase().replace(/[^A-Z0-9,;\s-]/g, "");

  return cleaned.replace(/([^\s,;]+)/g, (token) => {
    const parts = token.split("-");
    const baseCode = (parts[0] || "").slice(0, 5);
    if (parts.length === 1) {
      return baseCode;
    }
    const modifiers = parts
      .slice(1, 3)
      .map((mod) => mod.slice(0, 2));
    return [baseCode, ...modifiers].join("-");
  }).slice(0, 100);
};

/**
 * Sanitizes and caps ICD-10-CM diagnosis code input in real-time.
 * - Converts to uppercase
 * - Allows only [A-Z0-9.,;\s-]
 * - Caps each code token:
 *     - If dot present: at most 3 chars before dot, at most 4 chars after dot (e.g. E11.9, S06.0X1A)
 *     - If no dot: at most 7 characters (e.g. E119, S060X1A)
 * - Prevents entering continuous invalid strings.
 */
export const formatIcd10Input = (input?: string | null): string => {
  if (!input) return "";
  const cleaned = input.toUpperCase().replace(/[^A-Z0-9.,;\s-]/g, "");

  return cleaned.replace(/([^\s,;]+)/g, (token) => {
    if (token.includes(".")) {
      const [before, ...afterParts] = token.split(".");
      const after = afterParts.join("");
      return `${(before || "").slice(0, 3)}.${after.slice(0, 4)}`;
    }
    return token.slice(0, 7);
  }).slice(0, 100);
};

// ── 4. Charge Amount Validation ──
/**
 * Validates a Charge Amount in US healthcare billing UI.
 *
 * Rules:
 * - Only numeric non-negative amounts (e.g. 100, 100.50, 1250.99, 0.50)
 * - Up to 2 decimal places
 * - Rejects negative values, alphabetic letters, currency symbols ($), multiple dots
 * - Maximum value: 9999999.99
 *
 * UI error messages:
 * - "Charge amount can have up to 2 decimal places." when decimal places exceed 2
 * - "Please enter a valid charge amount." for all other invalid inputs
 */
export const validateChargeAmount = (
  val?: string | number | null,
  isRequired = false,
  requiredMessage = "Charge amount is required."
): string => {
  if (val === undefined || val === null) {
    return isRequired ? requiredMessage : "";
  }
  const strVal = String(val).trim();
  if (!strVal) {
    return isRequired ? requiredMessage : "";
  }

  // Reject invalid characters like letters, $, commas, negative signs, spaces
  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Please enter a valid charge amount.";
  }

  // Check multiple decimal points (e.g. 10..50, 10.5.0)
  if (strVal.split(".").length > 2) {
    return "Please enter a valid charge amount.";
  }

  // Check if decimal places exceed 2 (e.g. 100.999, 0.5555, 12.123)
  if (/^\d*\.\d{3,}$/.test(strVal)) {
    return "Charge amount can have up to 2 decimal places.";
  }

  // Valid pattern: whole integer or 1-2 decimal places (e.g. 100, 100.5, 100.50, 0.50, .50)
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Please enter a valid charge amount.";
  }

  const num = parseFloat(strVal);
  if (isNaN(num) || num < 0) {
    return "Please enter a valid charge amount.";
  }

  // Business maximum charge limit (e.g. $9,999,999.99)
  if (num > 9999999.99) {
    return "Please enter a valid charge amount.";
  }

  return "";
};

export const isValidChargeAmount = (val?: string | number | null): boolean => {
  return validateChargeAmount(val, false) === "";
};

// ── 5. Insurance Policy Details Validations ──
/**
 * Validates an insurance Authorization Number based on US healthcare practices.
 * Allows only alphanumeric characters (A–Z, a–z, 0–9), up to 30 characters.
 * Does not allow special characters or spaces. Does not enforce fixed length.
 * Does not require a value (optional). Trims leading and trailing spaces.
 */
export const validateAuthorizationNumber = (val?: string | number | null): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";
  if (!/^[A-Za-z0-9]{1,30}$/.test(strVal)) {
    return "Please enter a valid authorization number.";
  }
  return "";
};

/**
 * Validates Copay Amount (positive/non-negative monetary amount, up to 2 decimal places).
 */
export const validateCopayAmount = (val?: string | number | null): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Please enter a valid copay amount.";
  }
  if (strVal.split(".").length > 2 || /^\d*\.\d{3,}$/.test(strVal)) {
    return "Please enter a valid copay amount.";
  }
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Please enter a valid copay amount.";
  }
  const num = parseFloat(strVal);
  if (isNaN(num) || num < 0) {
    return "Please enter a valid copay amount.";
  }
  return "";
};

/**
 * Validates Coinsurance Percentage (numeric percentage between 0 and 100, up to 2 decimal places).
 */
export const validateCoinsurancePercentage = (val?: string | number | null): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Coinsurance percentage must be between 0 and 100.";
  }
  if (strVal.split(".").length > 2 || /^\d*\.\d{3,}$/.test(strVal)) {
    return "Coinsurance percentage must be between 0 and 100.";
  }
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Coinsurance percentage must be between 0 and 100.";
  }
  const num = parseFloat(strVal);
  if (isNaN(num) || num < 0 || num > 100) {
    return "Coinsurance percentage must be between 0 and 100.";
  }
  return "";
};

/**
 * Validates Annual Deductible amount (non-negative monetary amount, up to 2 decimal places).
 */
export const validateAnnualDeductible = (val?: string | number | null): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Please enter a valid annual deductible amount.";
  }
  if (strVal.split(".").length > 2 || /^\d*\.\d{3,}$/.test(strVal)) {
    return "Please enter a valid annual deductible amount.";
  }
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Please enter a valid annual deductible amount.";
  }
  const num = parseFloat(strVal);
  if (isNaN(num) || num < 0) {
    return "Please enter a valid annual deductible amount.";
  }
  return "";
};

/**
 * Validates Remaining Deductible amount.
 * Cannot be negative, max 2 decimals, and cannot exceed Annual Deductible when provided.
 */
export const validateRemainingDeductible = (
  val?: string | number | null,
  annualDeductibleVal?: string | number | null
): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Please enter a valid remaining deductible amount.";
  }
  if (strVal.split(".").length > 2 || /^\d*\.\d{3,}$/.test(strVal)) {
    return "Please enter a valid remaining deductible amount.";
  }
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Please enter a valid remaining deductible amount.";
  }
  const remainingNum = parseFloat(strVal);
  if (isNaN(remainingNum) || remainingNum < 0) {
    return "Please enter a valid remaining deductible amount.";
  }

  // Cross-field rule: Remaining cannot exceed Annual Deductible
  if (annualDeductibleVal !== undefined && annualDeductibleVal !== null) {
    const annualStr = String(annualDeductibleVal).trim();
    if (annualStr && /^(?:\d+|\d*\.\d{1,2})$/.test(annualStr)) {
      const annualNum = parseFloat(annualStr);
      if (!isNaN(annualNum) && remainingNum > annualNum) {
        return "Remaining deductible cannot exceed the annual deductible.";
      }
    }
  }

  return "";
};

/**
 * Validates Out-of-Pocket Limit amount (non-negative monetary amount, up to 2 decimal places).
 */
export const validateOutOfPocketLimit = (val?: string | number | null): string => {
  if (val === undefined || val === null) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  if (/[^0-9.]/.test(strVal) || strVal === ".") {
    return "Please enter a valid out-of-pocket limit.";
  }
  if (strVal.split(".").length > 2 || /^\d*\.\d{3,}$/.test(strVal)) {
    return "Please enter a valid out-of-pocket limit.";
  }
  if (!/^(?:\d+|\d*\.\d{1,2})$/.test(strVal)) {
    return "Please enter a valid out-of-pocket limit.";
  }
  const num = parseFloat(strVal);
  if (isNaN(num) || num < 0) {
    return "Please enter a valid out-of-pocket limit.";
  }
  return "";
};
